import i18n from '@/locales'
import router from '@/router'
import { ActionContext, GetterTree, MutationTree } from 'vuex'
import {
  DraftFieldError,
  DraftFieldType,
  isPublishingDraftPage,
  turboPublishCurrentDraft,
  validateDraftPage
} from '@/controllers/admin/draftPage'
import {SchedulerJobState, SchedulerJobType} from '@/interfaces/admin/draftPage'
import { getPage } from '@/controllers/admin'
import { postError } from '@/helpers/notification'
// Interfaces and Types
interface State {
  draftFieldErrors: Array<DraftFieldError>
  publishing: PublishingState,
  showDraftFieldErrors: boolean,
}

interface NewPollData {
  pageId: number,
  intervalId: number
}

interface DraftPagePublishRequest {
  draftPageId: number
}

interface PublishingState {
  hasSeenPublish: boolean,
  intervalId: number|null,
  pageId: number|null,
  publisherMessages: []|null,
  state: string|null,
  type: string|null
}

export enum MutationTypes {
  CLEAR_POLLING = 'CLEAR_POLLING',
  RECEIVED_NEW_PUBLISH_STATE = 'RECEIVED_NEW_PUBLISH_STATE',
  START_PUBLISH_POLLING = 'START_PUBLISH_POLLING',
  VALIDATED_DRAFT = 'VALIDATED_DRAFT',
  VIEWED_DRAFT_ERRORS = 'VIEWED_DRAFT_ERRORS'
}

type Context = ActionContext<State, any>

const PARAMS = {
  autoGenerateDraftPage: false,
  loadDraftSheets: false,
  loadDraftBomItems: false,
  loadDraftPageTagItems: false
}

const COMPLETED_JOB_STATES = [
  SchedulerJobState.COMPLETED.toString(),
  SchedulerJobState.COMPLETED_WITH_ERROR.toString()
]

const HIDDEN_DRAFT_VALIDATION_FIELD_TYPES = new Set<string>([
  DraftFieldType.TRANSLATION_DESCRIPTION,
  // Compatibility for older Turbo versions during a rolling deployment.
  'THREED_PART_NUMBER'
])

const state: State = {
  draftFieldErrors: [],
  publishing: {
    hasSeenPublish: false,
    intervalId: null,
    pageId: null,
    publisherMessages: null,
    state: null,
    type: null
  },
  showDraftFieldErrors: false
}

const getters: GetterTree<State, any> = {
  draftFieldErrors: (state) => state.draftFieldErrors,
  hasLastDraftPublishErrored: (state) => state?.publishing?.state == SchedulerJobState.COMPLETED_WITH_ERROR.toString(),
  /**
   * True if the page is being published via any publishing workflow.
   * Use this for general UI disabling during publishing operations.
   *
   * Covers:
   * - PUBLISH_DOCUMENTS_FOR_DRAFT_PAGE (normal draft publish)
   * - TURBO_PUBLISH_DOCUMENTS_FOR_DRAFT_PAGE (turbo draft publish)
   * - PUBLISH_PDF_IMPORT_PAGE (AI PDF Import publish)
   *
   * Does NOT cover:
   * - PUBLISH_ARCHIVED_DRAFT_PARENT (PLZ archive processing - use isDraftPageProcessingPlz)
   */
  isPagePublishing: (state) =>
    state?.publishing?.state == null
      ? false
      : ((state?.publishing?.type === SchedulerJobType.PUBLISH_DOCUMENTS_FOR_DRAFT_PAGE)
      || (state?.publishing?.type === SchedulerJobType.TURBO_PUBLISH_DOCUMENTS_FOR_DRAFT_PAGE)
      || (state?.publishing?.type === SchedulerJobType.PUBLISH_PDF_IMPORT_PAGE))
      && !COMPLETED_JOB_STATES.includes(state?.publishing?.state ?? ''),
  /**
   * True if the page is being loaded/published from a PLZ archive file.
   * This is a SEPARATE workflow from regular publishing.
   *
   * Use this for:
   * - Showing "Page being loaded from PLZ file" message
   * - PLZ-specific UI states
   *
   * Job type: PUBLISH_ARCHIVED_DRAFT_PARENT
   */
  isDraftPageProcessingPlz: (state) =>
    state?.publishing?.state == null
      ? false
      : state?.publishing?.type == SchedulerJobType.PUBLISH_ARCHIVED_DRAFT_PARENT && !COMPLETED_JOB_STATES.includes(state?.publishing?.state ?? ''),
  showDraftErrorModal: (state) => state.showDraftFieldErrors
}

const actions = {
  async clearPolling ({ commit }: Context) {
    commit(MutationTypes.CLEAR_POLLING)
  },
  /*
  Entry point to poll publishing page; new states for any listeners is available via getters.
  This will terminate and clear when publishing finishes. Rejects any request if the page is
  the same as the existing polled page id.
   */
  async pollPublishingPage({ commit }: Context, pageId: number) {
    const intervalId = setInterval(
      async () => {
        await poll()
      },
      5000
    )
    async function poll() {
      const response = await getPage(pageId)
      /*
      Delicate balance between polling intervals and the async /publish. 'hasSeenPublish' is present
      in the case the new router-view loads faster than the new schedule id is created and readable.
       */
      if (
        state.publishing?.hasSeenPublish &&
        (!response.draft || (response.draft && COMPLETED_JOB_STATES.includes(response.draft.draftSchedulerJobState)))
      ) {
        clearInterval(intervalId)
        if (response.draft && response.draft.draftSchedulerJobState) {
          if (response.draft.draftSchedulerJobState === SchedulerJobState.COMPLETED_WITH_ERROR.toString()) {
            postError({
              title: i18n.global.t('publishingErrors'),
            })
          }
        }
      }

      commit(MutationTypes.RECEIVED_NEW_PUBLISH_STATE, {
        hasSeenPublish: state.publishing?.hasSeenPublish
          ? state.publishing?.hasSeenPublish
          : (response.draft ? !!response.draft.draftSchedulerJobState : true),
        intervalId: intervalId,
        pageId: response.id,
        publisherMessages: response.draft ? response.draft.publisherMessages : null,
        state: response.draft ? response.draft.draftSchedulerJobState : SchedulerJobState.COMPLETED,
        type: response.draft ? response.draft.draftSchedulerJobType : null
      })
    }

    if (state.publishing?.pageId == pageId) return

    // Run poll immediately so that there is data for the initial state
    await poll()
    commit(MutationTypes.START_PUBLISH_POLLING, {
      intervalId: intervalId,
      pageId: pageId
    })
  },
  /*
   Stops all polling, regardless of completion. Expect to be handled by original invoker of 'pollPublishingPage'.
   */
  async publishDraftPage ({ commit, rootState, dispatch }: Context, payload: DraftPagePublishRequest) {
    try {
      const { draftPageId } = payload

      // Throw custom error toast and do not publish if publishing job is in progress
      const hasPublishRunning = await isPublishingDraftPage(rootState.draftPage.targetPageId, PARAMS)
      if (hasPublishRunning) {
        postError({
          title: i18n.global.t('publishingInProgress'),
        })
        return
      }

      // Initiate publishing of draft page
      await turboPublishCurrentDraft(draftPageId)
      await dispatch('pollPublishingPage', rootState.draftPage.targetPageId)

      // Redirect to Page Viewer
      await router.push({name: 'PageIllustration'})
    } catch (error) {
      postError({
        title: i18n.global.t('draftBomError'),
      })
    }
  },
  async validateDraft ({ commit, rootState }: Context, draftPageId: number) {
    const data = await validateDraftPage(draftPageId)
    const filteredData = data.filter((item) => !HIDDEN_DRAFT_VALIDATION_FIELD_TYPES.has(item.fieldType))
    const locales = rootState.locales.items
    filteredData.forEach((item) => {
      // @ts-ignore
      item.lang = locales.find(l => l.code4 === item.lang)?.name
      if (item.fieldType === DraftFieldType.SUPERSEDED_PART) {
        const itemIdx = item.instanceId - 1
        item.supersededPartNumber = rootState.draftPage.draftBomItems[itemIdx].supersededPartNumber
      }

    })

    commit(MutationTypes.VALIDATED_DRAFT, filteredData)
  },
  async viewedDraftErrors ({ commit }: Context) {
    commit(MutationTypes.VIEWED_DRAFT_ERRORS)
  }
}

const mutations: MutationTree<State> = {
  [MutationTypes.CLEAR_POLLING] (state) {
    const intervalId = state?.publishing?.intervalId ? state?.publishing?.intervalId : undefined

    if (intervalId) {
      clearInterval(intervalId)
      Object.assign(
        state.publishing,
        {
          hasSeenPublish: false,
          intervalId: null,
          pageId: null,
          publisherMessages: null,
          state: null
        }
      )
    }
  },
  [MutationTypes.RECEIVED_NEW_PUBLISH_STATE] (state, payload: PublishingState) {
    Object.assign(state.publishing, payload)
  },
  [MutationTypes.START_PUBLISH_POLLING] (state, payload: NewPollData) {
    Object.assign(
      state.publishing,
      {
        hasSeenPublish: false,
        intervalId: payload.intervalId,
        pageId: payload.pageId,
        publisherMessages: null,
        state: SchedulerJobState.SUBMITTED
      }
    )
  },
  [MutationTypes.VALIDATED_DRAFT] (state, payload: Array<DraftFieldError>) {
    state.draftFieldErrors = payload
    state.showDraftFieldErrors = true
  },
  [MutationTypes.VIEWED_DRAFT_ERRORS] (state) {
    state.showDraftFieldErrors = false
  }
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
}
